import { serverNowMs } from './time.js';

function scoreSignature(state) {
  const scores = Array.isArray(state?.scores) ? state.scores : [];
  const roundScores = Array.isArray(state?.roundScores) ? state.roundScores : [];
  const status = state?.status || {};
  const players = Array.isArray(state?.players) ? state.players : [];
  const aliveSig = players
    .map((player) => (player?.alive ? '1' : '0'))
    .join('');
  // Presence too, because the panel dims a player who is no longer in the game.
  // Without it the score panel is only redrawn when a SCORE or a death moves,
  // so somebody leaving mid-match changed nothing on screen until the next
  // point was won -- the flag was on the wire and in the client's state, and
  // the render was simply never asked for. Absent reads as present, so a state
  // that never carries the flag cannot flip the signature on its own.
  const presenceSig = players
    .map((player) => (player?.connected === false ? '0' : '1'))
    .join('');
  return [
    Number(state?.target) || 0,
    scores.join(','),
    roundScores.join(','),
    status.waitingNext ? 1 : 0,
    status.gameOver ? 1 : 0,
    Number(status.roundWinnerId) || 0,
    aliveSig,
    presenceSig
  ].join('|');
}

function overlayStateSignature(state) {
  const status = state?.status || {};
  return [
    status.running ? 1 : 0,
    status.waitingNext ? 1 : 0,
    status.gameOver ? 1 : 0,
    status.paused ? 1 : 0,
    Number(status.nextRoundAt) || 0,
    Number(status.matchRestartAt) || 0,
    Number(status.roundWinnerId) || 0,
    Number(status.matchWinnerId) || 0,
    status.matchTie ? 1 : 0
  ].join('|');
}

function standingsSignature(standings) {
  if (!Array.isArray(standings) || standings.length === 0) return '0';
  return standings.map((entry) => `${entry.id}:${entry.score}`).join('|');
}

function latestDeathSeq(state) {
  const events = Array.isArray(state?.deathEvents) ? state.deathEvents : [];
  if (events.length === 0) return 0;
  const seq = Number(events[0]?.seq);
  return Number.isFinite(seq) ? seq : 0;
}

function powerupIdSet(state, target = new Set()) {
  const ids = target;
  ids.clear();
  const powerups = Array.isArray(state?.powerups) ? state.powerups : [];
  for (const powerup of powerups) {
    const id = Number(powerup?.id);
    if (Number.isFinite(id)) ids.add(id);
  }
  return ids;
}

function alivePlayerCount(state) {
  const players = Array.isArray(state?.players) ? state.players : [];
  let alive = 0;
  for (const player of players) {
    if (player?.alive) alive += 1;
  }
  return alive;
}

// Match size is frozen into the engine config at the welcome edge. Room
// capacity is not match size: an eight-seat FFA or team room can legitimately
// run a one-player match, which is still a solo run everywhere else (scoring,
// field sizing, and team fallback). Older servers may omit the count fields,
// so the hydrated player array remains a safe compatibility fallback.
function isSoloMatch(config, state) {
  const humanCount = Number(config?.humanCount);
  const botCount = Number(config?.botCount);
  if (
    Number.isInteger(humanCount)
    && humanCount >= 0
    && Number.isInteger(botCount)
    && botCount >= 0
  ) {
    return humanCount + botCount === 1;
  }
  const players = Array.isArray(state?.players) ? state.players : [];
  return players.length === 1;
}

// Whether a room reaching its startable phase is a MOMENT, or just its resting
// state. The host's ready cue is worth playing only for the former.
//
// A one-seat room is startable from the instant you enter it, and startable
// again the instant a match ends -- there is no moment there at all. A room that
// opens at MIN 1 (the FFA preset the create form defaults to) has the same
// problem for the same reason: the host alone already satisfies it, so 'ready'
// says nothing about anybody arriving. For those, the moment worth announcing is
// the room FILLING instead. A room with a real minimum announces the edge into
// ready, which is where the host actually has a decision to make.
//
// Pure, and separated from the notifier for exactly that reason: everything else
// about the cue needs a DOM and an unlocked AudioContext, and this is the part
// with a rule in it.
function roomReadyIsAnnounceable({ minPlayersToStart, maxPlayers, connectedPlayers } = {}) {
  const seats = Number(maxPlayers);
  const minimum = Number(minPlayersToStart);
  if (!Number.isFinite(seats) || seats <= 1) return false;
  if (Number.isFinite(minimum) && minimum > 1) return true;
  const present = Number(connectedPlayers);
  return Number.isFinite(present) && present >= seats;
}

function remainingSeconds(targetAt, nowMs = serverNowMs()) {
  const target = Number(targetAt);
  if (!Number.isFinite(target) || target <= 0) return 0;
  return Math.max(0, Math.ceil((target - nowMs) / 1000));
}

export {
  scoreSignature,
  overlayStateSignature,
  standingsSignature,
  latestDeathSeq,
  powerupIdSet,
  alivePlayerCount,
  isSoloMatch,
  roomReadyIsAnnounceable,
  remainingSeconds
};
